import { Checkbox } from 'antd';
import { useState } from 'react';

import Button from '@/components/Button/Button';
import Input from '@/components/Inputs/Input';
import { useModalContext } from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import service from '@/services';
import { connectionErrorHandler } from '@/services/integrations/util';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { isEmptyString } from '@/utils/util';

import { CompoundConnectModal } from '../../components/ConnectModal';
import type { RichpanelInfo } from '../types';

type Props = {
  onConnectComplete: (workspace: RichpanelInfo) => void;
};

const DOCUMENTATION_URL =
  'https://deepbluedot.notion.site/Syncly-Richpanel-Webhook-29be324f208f8068acb4f726dcb74571?source=copy_link';

export const RichpanelConnectModal = (props: Props) => {
  const { closeModal } = useModalContext();
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [userChecked, setUserChecked] = useState(false);
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const handleConnectWorkspace = async () => {
    if (!currWorkspace) return;
    try {
      const richpanel = await service.richpanel.createRichpanelInfo(
        apiKey,
        currWorkspace.alias,
      );
      props.onConnectComplete(richpanel);
      closeModal();
    } catch (e) {
      setError(connectionErrorHandler(e));
    }
  };

  const isApiKeyValid = !isEmptyString(apiKey);

  return (
    <CompoundConnectModal
      title="Connect Richpanel"
      description="API key is required to connect to Richpanel"
      error={error}
      connectButtonDisabled={!userChecked || !isApiKeyValid}
      handleConnectWorkspace={handleConnectWorkspace}
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <Typo type="label2" weight="sb">
            1. Get API Key
          </Typo>
          <Typo type="body3" className="text-gray-600">
            Navigate to <strong>Settings &gt; API Keys</strong> in your
            Richpanel dashboard and copy API key.
          </Typo>
          <Input
            placeholder="Type API key"
            sizeType="m"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Typo type="label2" weight="sb">
              2. Connect Webhook
            </Typo>
            <Typo type="body3" className="text-gray-600">
              Follow the step-by-step guide in our documentation to register
              Syncly's webhook
            </Typo>
          </div>
          <div className="flex flex-col gap-5">
            <Button
              variant="outlined"
              sizeType="m"
              className="w-fit"
              onClick={() => window.open(DOCUMENTATION_URL, '_blank')}
            >
              View documents
            </Button>
            <div className="flex gap-2 items-center">
              <Checkbox
                id="user-checked"
                checked={userChecked}
                onChange={(e) => setUserChecked(e.target.checked)}
              />
              <label htmlFor="user-checked">
                <Typo type="body3" className="text-gray-800">
                  I have completed the webhook integration.
                </Typo>
              </label>
            </div>
          </div>
        </div>
      </div>
    </CompoundConnectModal>
  );
};
